import { CloseOutlined, CopyOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Form, Input, message, Modal, Select, Space, Switch, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { serverFetch } from "@server";
import { tokenVids } from "@extensions/enums";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";

const MessagesToken = ({ onClose, onSuccess, id, providers, spaces = [] }) => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(null);
  const [form] = Form.useForm();

  const spaceOptions = spaces.filter(s => s.vid === 1).map(s => ({ value: s.id, label: s.name }));

  useEffect(() => {
    if (id === 0) {
      const defaultValues = { id, name: "", code: "", active: true, vid: 1, idx: 0, provider: null, spaceId: null };
      setItem(defaultValues);
      form?.setFieldsValue(defaultValues);
    } else if (id > 0) {
      setLoading(true);
      serverFetch(`token/${id}`)
        .then(data => {
          setItem(data);
          form.setFieldsValue(data);
          setLoading(false);
        })
        .catch(e => {
          console.error("Ошибка загрузки токена:", e.userMessage);
          setLoading(false);
        });
    }
  }, [id, form]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const method = id === 0 ? "POST" : "PUT";
      serverFetch("token", { method, bodyData: item })
        .then(() => {
          onSuccess && onSuccess();
          onClose && onClose();
        })
        .catch(e => {
          setLoading(false);
          console.error("Ошибка сохранения токена:", e.userMessage);
        });
    } catch (errorInfo) {
      console.log("Failed:", errorInfo);
    }
  };

  const handleFormChange = values => setItem({ ...item, ...values });
  const handleClose = () => onClose(false);
  const handleCopyCode = () => {
    const code = item?.code ?? "";
    navigator.clipboard.writeText(code).then(
      () => message.success("Код скопирован в буфер обмена"),
      () => message.error("Не удалось скопировать")
    );
  };
  const availableProviders = providers.filter(p => p.vid === item?.vid).map(p => ({ value: p.id, label: p.name }));
  const providerUrl = providers.find(p => p.id === item?.provider)?.url;
  const providerDescription = providers.find(p => p.id === item?.provider)?.description;
  const isCodeReadOnly = item?.vid === 201;
  const isSiteVid = item?.vid === 201;
  const codePlaceholder = id === 0 && isSiteVid ? "Код будет сгенерирован" : "Введите код";

  const widgetBaseUrl = process.env.REACT_APP_WIDGET_BASE_URL || "https://cloud2.itokna.ru";
  const embedScript = item?.code
    ? `<script src="${widgetBaseUrl}/widget.js?tokenCode=${encodeURIComponent(item.code)}"></script>`
    : "";
  const handleCopyEmbed = () => {
    if (!embedScript) return;
    navigator.clipboard.writeText(embedScript).then(
      () => message.success("Код скопирован в буфер обмена"),
      () => message.error("Не удалось скопировать")
    );
  };

  const sheetTitle = id === 0 ? "Новый канал" : "Канал";

  const formProps = isMobile
    ? { layout: "vertical", colon: false, style: { width: "100%", maxWidth: "100%" } }
    : { layout: "vertical" };

  const tokenForm = (
    <Form
      form={form}
      {...formProps}
      initialValues={{ name: "", code: "", active: true, vid: 0, idx: 0, provider: null, spaceId: null }}
      onValuesChange={handleFormChange}
    >
      <Form.Item name="name" label="Наименование">
        <Input placeholder="Введите наименование" />
      </Form.Item>
      <Form.Item name="code" label="Код">
        <Input
          placeholder={codePlaceholder}
          readOnly={isCodeReadOnly}
          addonAfter={
            <Tooltip title="Копировать в буфер">
              <Button type="button" size="small" icon={<CopyOutlined />} onClick={handleCopyCode} disabled={!(item?.code && item.code.trim())} />
            </Tooltip>
          }
        />
      </Form.Item>
      {isSiteVid && (
        <Form.Item label="Код который надо разместить на сайте">
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Input.TextArea readOnly value={embedScript} rows={2} style={{ fontFamily: "monospace", fontSize: 12, flex: 1 }} />
            <Tooltip title="Копировать код">
              <Button type="primary" icon={<CopyOutlined />} onClick={handleCopyEmbed} disabled={!embedScript} />
            </Tooltip>
          </div>
        </Form.Item>
      )}
      {providerDescription && <Form.Item>{providerDescription}</Form.Item>}
      <Form.Item name="idx" label="Порядок">
        <Input type="number" placeholder="Порядок" />
      </Form.Item>
      <Form.Item name="spaceId" label="Киберменеджер">
        <Select placeholder="Выберите киберменеджера" options={spaceOptions} allowClear />
      </Form.Item>
      <Form.Item name="vid" label="Вид">
        <Select placeholder="Выберите вид токена" options={tokenVids.filter(i => i.value)} disabled={id > 0} />
      </Form.Item>
      {availableProviders.length ? (
        <Form.Item name="provider" label="Провайдер" dependencies={["vid"]}>
          <Select options={availableProviders} />
        </Form.Item>
      ) : null}
      {providerUrl ? (
        <Form.Item>
          <a href={providerUrl} target="_blank" rel="noreferrer">
            Подключить провайдера
          </a>
        </Form.Item>
      ) : null}
      <Form.Item name="active" label="Активен" valuePropName="checked">
        <Switch />
      </Form.Item>
    </Form>
  );

  if (isMobile) {
    return (
      <div
        className="misoft-modal-overlay company-base-overlay--mobile"
        onClick={e => e.target === e.currentTarget && handleClose()}
      >
        <div className="misoft-modal-content company-base-sheet--mobile" onClick={e => e.stopPropagation()}>
          <div className="company-base-sheet-header">
            <span className="company-base-sheet-title">{sheetTitle}</span>
            <Space size="small" className="company-base-sheet-actions">
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={loading}
                aria-label="Сохранить"
                className="company-base-header-icon-btn"
              />
              <Button
                type="default"
                icon={<CloseOutlined />}
                onClick={handleClose}
                aria-label="Закрыть"
                className="company-base-header-icon-btn"
              />
            </Space>
          </div>
          <div className="company-base-sheet-body">{tokenForm}</div>
        </div>
      </div>
    );
  }

  return (
    <Modal title="Канал" open onOk={handleSave} onCancel={handleClose} okText="Сохранить" cancelText="Отмена" confirmLoading={loading}>
      {tokenForm}
    </Modal>
  );
};

export default MessagesToken;
